import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Upload, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../services/api';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const { Option } = Select;

const CertificationEdit: React.FC = () => {
    const { skuId, id } = useParams<{ skuId: string; id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                await api.post(`/skus/${skuId}/certifications/`, data);
                message.success('认证已创建');
                navigate(`/sku-management/edit/${skuId}`);
            } else {
                await api.put(`/skus/${skuId}/certifications/${id}/`, data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        if (!isNew) {
            fetchCertification();
        }
    }, [id]);

    const fetchCertification = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/skus/${skuId}/certifications/${id}/`);
            const data = res.data;
            form.setFieldsValue({
                ...data,
                issue_date: data.issue_date ? dayjs(data.issue_date) : null,
                expiry_date: data.expiry_date ? dayjs(data.expiry_date) : null,
            });
        } catch (error) {
            message.error('加载失败');
            navigate(`/sku-management/edit/${skuId}`);
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            const saveData = {
                ...values,
                issue_date: values.issue_date ? values.issue_date.format('YYYY-MM-DD') : null,
                expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
            };
            await api.post(`/skus/${skuId}/certifications/`, saveData);
            message.success('认证已创建');
            navigate(`/sku-management/edit/${skuId}`);
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '新增认证' : '编辑认证'}
            breadcrumbs={[
                { title: 'SKU管理', path: '/sku-management' },
                { title: 'SKU详情', path: `/sku-management/edit/${skuId}` },
                { title: isNew ? '新增认证' : '编辑认证' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Form.Item name="cert_type" label="认证类型" rules={[{ required: true }]}>
                    <Select>
                        <Option value="CE">CE</Option>
                        <Option value="FCC">FCC</Option>
                        <Option value="RoHS">RoHS</Option>
                        <Option value="UL">UL</Option>
                        <Option value="FDA">FDA</Option>
                        <Option value="ISO">ISO</Option>
                        <Option value="Other">其他</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="cert_number" label="认证编号">
                    <Input placeholder="认证编号" />
                </Form.Item>
                <Form.Item name="issuing_authority" label="发证机构">
                    <Input placeholder="发证机构" />
                </Form.Item>
                <Form.Item name="issue_date" label="签发日期">
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="expiry_date" label="过期日期">
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="status" label="状态" initialValue="valid">
                    <Select>
                        <Option value="valid">有效</Option>
                        <Option value="expired">过期</Option>
                        <Option value="pending">待审核</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="notes" label="备注">
                    <Input.TextArea rows={3} />
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default CertificationEdit;
